/**
 * Generate a downloadable .ics (iCalendar) file for a weekly-recurring event
 * anchored to UK local time (Europe/London), so 4 PM UK stays 4 PM UK across
 * British Summer Time changes. Works with Google Calendar, Apple Calendar,
 * Outlook, Samsung Calendar, etc.
 *
 * @param {object} opts
 *   title       — event title
 *   description — long description (multi-line supported)
 *   location    — venue / URL / method
 *   weekday     — 0..6 (Sun..Sat) — day of week for the recurrence
 *   ukHour      — local UK hour (0..23) — e.g. 16 for 4 PM, 18 for 6 PM
 *   ukMinute    — local UK minute (0..59)
 *   durationHrs — length of session in hours (default 1)
 *   remindMins  — how many minutes before to remind (default 60)
 *   filename    — .ics filename to download
 */
export const downloadWeeklyIcs = ({
  title,
  description = "",
  location = "",
  weekday,
  ukHour,
  ukMinute = 0,
  durationHrs = 1,
  remindMins = 60,
  filename = "islamic-cure-care.ics",
}) => {
  // Find the next occurrence of the target weekday.
  const today = new Date();
  const daysAhead = (weekday - today.getDay() + 7) % 7 || 7;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + daysAhead);
  const yyyy = startDate.getFullYear();
  const mm = String(startDate.getMonth() + 1).padStart(2, "0");
  const dd = String(startDate.getDate()).padStart(2, "0");
  const hh = String(ukHour).padStart(2, "0");
  const mi = String(ukMinute).padStart(2, "0");

  const endHour = ukHour + Math.floor((ukMinute + durationHrs * 60) / 60);
  const endMinute = (ukMinute + durationHrs * 60) % 60;
  const eh = String(endHour).padStart(2, "0");
  const em = String(endMinute).padStart(2, "0");

  const startLocal = `${yyyy}${mm}${dd}T${hh}${mi}00`;
  const endLocal = `${yyyy}${mm}${dd}T${eh}${em}00`;
  const nowStamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

  const uid = `orgroup-${weekday}-${hh}${mi}-${Date.now()}@islamiccureandcare.co.uk`;

  const esc = (s) =>
    String(s || "").replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");

  const bydayCode = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][weekday];

  // Minimal but correct VTIMEZONE for Europe/London with BST/GMT transitions.
  const vtimezone = [
    "BEGIN:VTIMEZONE",
    "TZID:Europe/London",
    "BEGIN:DAYLIGHT",
    "TZOFFSETFROM:+0000",
    "TZOFFSETTO:+0100",
    "TZNAME:BST",
    "DTSTART:19700329T010000",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
    "END:DAYLIGHT",
    "BEGIN:STANDARD",
    "TZOFFSETFROM:+0100",
    "TZOFFSETTO:+0000",
    "TZNAME:GMT",
    "DTSTART:19701025T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
    "END:STANDARD",
    "END:VTIMEZONE",
  ].join("\r\n");

  const vevent = [
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${nowStamp}`,
    `DTSTART;TZID=Europe/London:${startLocal}`,
    `DTEND;TZID=Europe/London:${endLocal}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${bydayCode}`,
    `SUMMARY:${esc(title)}`,
    `DESCRIPTION:${esc(description)}`,
    `LOCATION:${esc(location)}`,
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `TRIGGER:-PT${remindMins}M`,
    `DESCRIPTION:${esc(title)}`,
    "END:VALARM",
    "END:VEVENT",
  ].join("\r\n");

  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Islamic Cure & Care//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    vtimezone,
    vevent,
    "END:VCALENDAR",
  ].join("\r\n");

  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default downloadWeeklyIcs;
